/*****************************************************************
 *  CUSTOMER BUTLER - WHAT THIS PERSON'S LEVEL SHOWS ON SCREEN     *
 *  File: apps/web/src/pages/customer/customerAccessView.js        *
 *  What it does: turns the person's Customer Butler level (set    *
 *  on the HR Team page, answered by the server's door             *
 *  GET /api/customer/access/me) into plain yes/no answers every   *
 *  customer page asks before drawing a button: may they add,      *
 *  edit, take a note off, change the setup?                       *
 *  Reads: the "my access" door, and (with no signal only) this    *
 *  device's memory of the last answer for this same login.        *
 *  Writes: that one device memory, nothing else.                  *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - THE RULE: WHAT EACH LEVEL SHOWS                    *
 *  SECTION 2 - ASKING THE SERVER (and the no-signal memory)       *
 *  SECTION 3 - SHARING THE ANSWER WITH EVERY PAGE PART            *
 *****************************************************************/

// WHY (2026-10-14, James's Honest UI rule, via Fable): "a button must never
// promise what the server will refuse". The server door decides; these pages
// only draw what that same level allows, so nobody taps "Add customer" and
// gets a refusal, and a person with no access is told so in plain words
// instead of "check the connection".

import { createContext, useContext } from 'react';
import { authFetch, API } from '../../lib/api';

/*****************************************************************
 *  SECTION 1 - THE RULE: WHAT EACH LEVEL SHOWS                    *
 *  One copy. The server's levels (customerAccess.js on the        *
 *  server) are the same four words.                               *
 *****************************************************************/

export const LEVEL_NONE = 'none';       // sees nothing in Customer Butler
export const LEVEL_LOOKUP = 'lookup';   // reads only
export const LEVEL_OFFICE = 'office';   // reads, adds and edits
export const LEVEL_MANAGER = 'manager'; // everything, including take-off and setup

// What each level is shown. Mirrors the server: lookup=read, office=read+write,
// manager=read+write+delete+manage. Taking a note off is a soft delete.
const SHOWN_BY_LEVEL = Object.freeze({
  none: Object.freeze({ mayOpen: false, mayWrite: false, mayTakeOff: false, mayChangeSetup: false }),
  lookup: Object.freeze({ mayOpen: true, mayWrite: false, mayTakeOff: false, mayChangeSetup: false }),
  office: Object.freeze({ mayOpen: true, mayWrite: true, mayTakeOff: false, mayChangeSetup: false }),
  manager: Object.freeze({ mayOpen: true, mayWrite: true, mayTakeOff: true, mayChangeSetup: true }),
});

/* ---- describeWhatLevelShows(level) ------------------------------
   In: a level word.  Out: { level, mayOpen, mayWrite, mayTakeOff,
   mayChangeSetup }. An unknown word shows nothing (fails closed). */
export function describeWhatLevelShows(level) {
  const knownLevel = Object.prototype.hasOwnProperty.call(SHOWN_BY_LEVEL, level) ? level : LEVEL_NONE;

  return { level: knownLevel, ...SHOWN_BY_LEVEL[knownLevel] };
}

/*****************************************************************
 *  SECTION 2 - ASKING THE SERVER (and the no-signal memory)       *
 *****************************************************************/

const ACCESS_DOOR_PATH = '/customer/access/me';         // the server's "what may I do?" door
const DEVICE_MEMORY_PREFIX = 'cb.access.lastAnswer.';   // + the login's user id

/* ---- rememberAnswerOnDevice(userId, level) ----------------------
   Keeps the last real answer for THIS login only, so a phone with
   no signal can still open what the person could open a moment
   ago. Never throws (private mode just means no memory).        */
function rememberAnswerOnDevice(userId, level) {
  if (!userId) {
    return;
  }

  try {
    window.localStorage.setItem(DEVICE_MEMORY_PREFIX + userId, level);
  } catch (storageProblem) {
    /* no memory on this device: fine */
  }
}

/* ---- readAnswerFromDevice(userId) -- the last answer, or null ---- */
function readAnswerFromDevice(userId) {
  if (!userId) {
    return null;
  }

  try {
    return window.localStorage.getItem(DEVICE_MEMORY_PREFIX + userId);
  } catch (storageProblem) {
    return null;
  }
}

/* ---- askMyCustomerAccess(userId) --------------------------------
   Asks the server once. Out: a Promise of
     { state: 'answered', level }            the server said
     { state: 'no-signal-remembered', level } no signal; last answer on this device
     { state: 'no-signal' }                  no signal, nothing remembered
     { state: 'trouble' }                    the server answered oddly
   Never throws.                                                 */
export async function askMyCustomerAccess(userId) {
  let doorResponse = null;

  try {
    doorResponse = await authFetch(`${API}${ACCESS_DOOR_PATH}`);
  } catch (networkProblem) {
    const rememberedLevel = readAnswerFromDevice(userId);

    if (rememberedLevel) {
      return { state: 'no-signal-remembered', level: rememberedLevel };
    }

    return { state: 'no-signal' };
  }

  if (!doorResponse.ok) {
    return { state: 'trouble' };
  }

  let doorJson = null;

  try {
    doorJson = await doorResponse.json();
  } catch (notJson) {
    return { state: 'trouble' };
  }

  const answeredLevel = describeWhatLevelShows(doorJson && doorJson.level).level;

  rememberAnswerOnDevice(userId, answeredLevel);

  return { state: 'answered', level: answeredLevel };
}

/*****************************************************************
 *  SECTION 3 - SHARING THE ANSWER WITH EVERY PAGE PART            *
 *  The gate (CustomerAccessGate.js) asks once per page and puts   *
 *  the answer here; buttons deep in a page read it with           *
 *  useCustomerAccessView(). Outside a gate nothing is offered.    *
 *****************************************************************/

export const CustomerAccessContext = createContext(describeWhatLevelShows(LEVEL_NONE));

/* ---- useCustomerAccessView() -- what this person may be shown ---- */
export function useCustomerAccessView() {
  return useContext(CustomerAccessContext);
}
